import { useState } from "react";

import { generatePassword, passwordStrength } from "@ext/crypto/generator";
import { isValidSecret, normalizeSecret } from "@ext/crypto/totp";
import type { LoginItemData } from "@ext/types";

import type { DecryptedItem } from "../auth";
import { TotpDisplay } from "../components/TotpDisplay";

interface Props {
  item: DecryptedItem | null; // null = new item
  onSave: (data: LoginItemData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
}

const EMPTY: LoginItemData = {
  type: "login",
  name: "",
  url: "",
  username: "",
  password: "",
  notes: "",
  totp_secret: null,
  folder_id: null,
  favorite: false,
  tags: [],
};

export function Editor({ item, onSave, onDelete, onCancel }: Props) {
  const [data, setData] = useState<LoginItemData>(
    item ? (item.data as LoginItemData) : EMPTY,
  );
  const [showPw, setShowPw] = useState(!item); // show password for new items
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function patch(p: Partial<LoginItemData>) {
    setData({ ...data, ...p });
  }

  function generate() {
    patch({
      password: generatePassword({
        length: 20,
        lowercase: true,
        uppercase: true,
        digits: true,
        symbols: true,
        excludeAmbiguous: true,
      }),
    });
    setShowPw(true);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!data.name.trim() && !data.url.trim()) {
      setError("Wpisz nazwę albo URL");
      return;
    }
    setBusy(true);
    try {
      await onSave(data);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function remove() {
    if (!onDelete) return;
    if (!confirm("Usunąć ten wpis?")) return;
    setBusy(true);
    try {
      await onDelete();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="header">
        <button className="back-btn" onClick={onCancel} aria-label="Anuluj">
          ‹ Anuluj
        </button>
        <h1>{item ? "Edytuj wpis" : "Nowy wpis"}</h1>
      </div>

      <form className="main" onSubmit={save}>
        <div className="field">
          <label>Nazwa</label>
          <input
            value={data.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder="np. Google"
            autoCapitalize="words"
            autoFocus={!item}
          />
        </div>

        <div className="field">
          <label>URL</label>
          <input
            type="url"
            value={data.url}
            onChange={(e) => patch({ url: e.target.value })}
            placeholder="https://example.com"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="url"
          />
        </div>

        <div className="field">
          <label>Login / e-mail</label>
          <input
            value={data.username}
            onChange={(e) => patch({ username: e.target.value })}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
          />
        </div>

        <div className="field">
          <label>Hasło ({passwordStrength(data.password)})</label>
          <div className="row">
            <input
              type={showPw ? "text" : "password"}
              value={data.password}
              onChange={(e) => patch({ password: e.target.value })}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="secondary"
              onClick={() => setShowPw(!showPw)}
              style={{ width: 50, padding: "12px 0", flexShrink: 0 }}
              aria-label={showPw ? "Ukryj" : "Pokaż"}
            >
              {showPw ? "🙈" : "👁"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={generate}
              style={{ width: 50, padding: "12px 0", flexShrink: 0 }}
              aria-label="Generuj"
            >
              ⚡
            </button>
          </div>
        </div>

        <div className="field">
          <label>Klucz 2FA (Base32 lub otpauth://)</label>
          <input
            type="text"
            value={data.totp_secret ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              patch({ totp_secret: raw ? normalizeSecret(raw) : null });
            }}
            placeholder="JBSWY3DPEHPK3PXP albo otpauth://…"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {data.totp_secret && isValidSecret(data.totp_secret) && (
            <div style={{ marginTop: 8 }}>
              <TotpDisplay secret={data.totp_secret} />
            </div>
          )}
        </div>

        <div className="field">
          <label>Notatki</label>
          <textarea
            rows={4}
            value={data.notes}
            onChange={(e) => patch({ notes: e.target.value })}
            style={{ minHeight: 100, resize: "vertical" }}
          />
        </div>

        {error && <div className="error">{error}</div>}

        <button
          type="submit"
          className="btn-block"
          disabled={busy}
          style={{ marginBottom: item ? 10 : 0 }}
        >
          {busy ? "Zapisuję…" : item ? "Zapisz zmiany" : "Utwórz wpis"}
        </button>

        {item && onDelete && (
          <button
            type="button"
            className="danger btn-block"
            onClick={remove}
            disabled={busy}
          >
            Usuń wpis
          </button>
        )}
      </form>
    </>
  );
}
